import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch, nexusBearerFetch } from '../../nexus/nexusClient';
import { useAuth } from '../../auth/AuthProvider';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import {
  hostingDomainsFixture,
  hostingMailboxesFixture,
  promoteFixture,
} from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-hosting-ops.md
 *
 * Customer-Picker, Domain-Liste, Staging→Final-Promote, Mailbox-CRUD.
 *
 * Mix aus M2M-Pfad (/v1/admin/customers) und Bearer-Pfad (/v1/hosting/*).
 * Beide laufen über die jeweiligen BFF-Proxies; `nexusFetch` = M2M,
 * `nexusBearerFetch` = Operator-Bearer.
 */

function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET /v1/hosting/accounts/:account_id/domains */
export function useHostingDomains(accountId) {
  const auth = useAuth();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-hosting-domains', accountId, fixturesMode ? 'fixture' : 'live'],
    enabled: !!accountId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return hostingDomainsFixture(accountId);
      }
      const res = await nexusBearerFetch(
        `/v1/hosting/accounts/${encodeURIComponent(accountId)}/domains`,
        { headers: authHeader(auth.getAccessToken()) },
      );
      const list = res?.domains ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { domains: list, raw: res };
    },
    staleTime: 15_000,
  });
}

/** GET /v1/hosting/accounts/:account_id/mailboxes */
export function useHostingMailboxes(accountId) {
  const auth = useAuth();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-hosting-mailboxes', accountId, fixturesMode ? 'fixture' : 'live'],
    enabled: !!accountId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return hostingMailboxesFixture(accountId);
      }
      const res = await nexusBearerFetch(
        `/v1/hosting/accounts/${encodeURIComponent(accountId)}/mailboxes`,
        { headers: authHeader(auth.getAccessToken()) },
      );
      const list = res?.mailboxes ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { mailboxes: list, raw: res };
    },
    staleTime: 15_000,
  });
}

/** POST /v1/hosting/accounts/:account_id/promote-domain — { domain } */
export function usePromoteDomain() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { accountId, domain }) =>
      nexusBearerFetch(
        `/v1/hosting/accounts/${encodeURIComponent(accountId)}/promote-domain`,
        {
          method: 'POST',
          body: { domain },
          headers: authHeader(auth.getAccessToken()),
        },
      ),
    fixture: ({ domain }) => promoteFixture(domain),
    invalidate: ['nexus-hosting-domains'],
    successMessage: (_res, { domain }) => `Promote zu ${domain} angestoßen.`,
  });
}

/** POST /v1/hosting/:account_id/mailboxes — { username, password, quota?, ... } */
export function useCreateMailbox() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { accountId, ...body }) =>
      nexusBearerFetch(`/v1/hosting/${encodeURIComponent(accountId)}/mailboxes`, {
        method: 'POST',
        body,
        headers: authHeader(auth.getAccessToken()),
      }),
    fixture: ({ username }) => ({ ok: true, address: username }),
    invalidate: ['nexus-hosting-mailboxes'],
    successMessage: (_res, { username }) => `Mailbox ${username} angelegt.`,
  });
}

/** DELETE /v1/hosting/:account_id/mailboxes/:address */
export function useDeleteMailbox() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { accountId, address }) =>
      nexusBearerFetch(
        `/v1/hosting/${encodeURIComponent(accountId)}/mailboxes/${encodeURIComponent(address)}`,
        { method: 'DELETE', headers: authHeader(auth.getAccessToken()) },
      ),
    fixture: ({ address }) => ({ ok: true, address }),
    invalidate: ['nexus-hosting-mailboxes'],
    successMessage: (_res, { address }) => `Mailbox ${address} gelöscht.`,
  });
}

/**
 * Helper für den Customer-Picker — schmaler Wrapper um
 * /v1/admin/customers?search=… mit kürzerem Stale-Window.
 */
export function useCustomerSearch(search) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-hosting-customer-search', search, fixturesMode ? 'fixture' : 'live'],
    enabled: search.length >= 2,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return { customers: hostingDomainsFixture.__sampleCustomers(search) };
      }
      const qs = new URLSearchParams();
      qs.set('search', search);
      qs.set('limit', '15');
      const res = await nexusFetch(`/v1/admin/customers?${qs.toString()}`);
      const list = res?.customers ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { customers: list };
    },
    staleTime: 10_000,
  });
}

/**
 * GET /v1/admin/customers/:id — wir brauchen das hier nur für die Service-
 * Liste pro Customer (Picker-Step 2). Eigene Query, damit der Picker nicht
 * gegen die Customers-View-Query kollidiert.
 */
export function useCustomerHostingServices(customerId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-hosting-customer-services', String(customerId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!customerId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return hostingDomainsFixture.__sampleServices(customerId);
      }
      const res = await nexusFetch(`/v1/admin/customers/${encodeURIComponent(customerId)}`);
      const services = res?.services ?? [];
      // Live-Shape: services[].plugin_id|kind. Wir filtern auf hosting* damit
      // VPS/Game/Cloud nicht in der Hosting-Ops-Auswahl auftauchen.
      return {
        services: services.filter((s) =>
          (s.plugin_id ?? s.kind ?? '').toLowerCase().startsWith('hosting'),
        ),
      };
    },
    staleTime: 15_000,
  });
}
